/**
 * AudioPreferences.js
 * Talkroute
 *
 * Created by Vel Genov on 2/4/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  DEFAULT_AUDIO_SOURCE,
  setCallNotificationSound,
  setDefaultAudioInput,
  setDefaultAudioOutput,
  setTextNotificationSound,
  toggleTestMic
} from "../../actions/preferencesActions";
import uiString from "../../constants/uiString";
import MicLevelVisualizer from "./MicLevelVisualizer";
import VolumeSlider from "./VolumeSlider";
import FilePathHelper from "@helpers/functions/filePathHelper";
import { domIds } from "@constants/testConstants";
import { isFirefox, isSafari } from "@constants/appConstants";
import { makeStyles } from "@material-ui/core";
import ErrorIcon from '@material-ui/icons/Error';
import DropdownComponent from "../PresentationComponents/DropdownComponent";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import ToggledRadioButton from "../PresentationComponents/FormComponents/ToggledRadioButton";
import Tooltip from '@components/PresentationComponents/FormComponents/Tooltip';
import { UnderlinedTitle } from "@components/PresentationComponents/UnderlinedTitle";
import { filterDevices } from "@helpers/audioPreferences";
import { AudioOptions } from "@models/AudioFiles.models";
import { callNotificationOptionName, deviceTypes, testSoundFilePath, textNotificationOptionName } from "./constants";

let notifyPlayer: any;
let audioPlayer: any;

const WarningForSafariOrFirefox = () => (
  <Tooltip styling="standard" style={{marginLeft: 5}} title={uiString.preferences.SAFARI_OR_FIREFOX_AUDIO_DEVICE_INFORMATION}>
    <ErrorIcon color="primary"/>
  </Tooltip>
);
/**
 * Displays and sets the audio preferences. Currently audio input and output
 */
const NotificationsAndSounds: React.FC = () => {
    const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([])
    const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([])
    const [playingTestSound, setPlayingTestSound] = useState(false)
    const isEnabledTestMic = useAppSelector(state => state.preferences.isEnabledTestMic)
    const defaultAudioInput = useAppSelector(state => state.preferences.defaultAudioInput)
    const defaultAudioOutput = useAppSelector(state => state.preferences.defaultAudioOutput)
    const defaultAudioOutputVolume = useAppSelector(state => state.preferences.defaultAudioOutputVolume)
    const defaultAudioInputVolume = useAppSelector(state => state.preferences.defaultAudioInputVolume)
    const selectedCallNotificationSound = useAppSelector(state => state.preferences.selectedCallNotificationSound)
    const selectedTextNotificationSound = useAppSelector(state => state.preferences.selectedTextNotificationSound)
    const dispatch = useDispatch()
    const classes = useStyles()

/**
 * Get a list of the available audio devices on the system
 *
 */
const getAudioDevices = () => {
  navigator.mediaDevices
    .enumerateDevices()
    .then(gotDevices)
    .catch(handleError);
};

/**
 * Called when a list of audio devices is available
 *
 * Starts filterDevices()
 */
const gotDevices = (devices: MediaDeviceInfo[]) => {
  const { filteredInputDevices, filteredOutputDevices } = filterDevices(devices);
  setInputDevices(filteredInputDevices);
  setOutputDevices(filteredOutputDevices);
};

/**
 * Handles any errors that accur when getting devices
 */
const handleError = (error) => {
  console.error(error);
};

/**
 * Shows a dropdown with the available audio devices
 *
 * @param label the dropdown label
 * @param devices a list of devices to display
 * @param selected the currently selected device
 * @type deviceTypes input or output
 */
const showAudioOptions: React.FC<AudioOptions> = ({ label, devices, selected, type, forceSelect }) => {
  let dropdownOptions: { value: string; text: string }[] | [] = [];
  if (Array.isArray(devices) && devices.length > 0) {
    dropdownOptions = devices.map((device) => ({
      value: device.deviceId,
      text: device.label,
    }));
  }

  const standardDefaultValue = dropdownOptions.length
    ? selected || DEFAULT_AUDIO_SOURCE
    : undefined;
  const forcedDefaultValue = dropdownOptions[0]?.value;

  const selectedValue = forceSelect
    ? forcedDefaultValue
    : standardDefaultValue;
  
  const dropdownId = type === deviceTypes.INPUT
    ? domIds.AUDIO_PREFERENCES_INPUT_DEVICES_DROPDOWN
    : domIds.AUDIO_PREFERENCES_OUTPUT_DEVICES_DROPDOWN

  return (
    <div style={{ padding: "15px 0 10px 0" }}>
      <div style={{ padding: "5px 0" }} className="none-select-text">
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        {dropdownOptions.length !== 0 && 
          <DropdownComponent
            id={dropdownId}
            className="text-light"
            value={selectedValue}
            name={type}
            options={dropdownOptions}
            onChange={({target: {name,value}}) => setDefaultDevice(name, value)}
            smallIcon
          />
        }
        {type === deviceTypes.INPUT
          ? showTestInput()
          : showTestOutput()}
      </div>
    </div>
  );
};

/**
 * Shows radio option of Test Input
 */
const showTestInput = () => {
  return (
    <ToggledRadioButton 
      label={uiString.preferences.TEST_INPUT} 
      checked={isEnabledTestMic}
      onChange={handleTestMic}
      className={classes.radioButton}
    />
  );
};

/**
 * Shows test output option
 */
const showTestOutput = () => {
  return (
    <ToggledRadioButton 
      label={uiString.preferences.TEST_OUTPUT} 
      checked={playingTestSound}
      onChange={handleTestOutput}
      className={classes.radioButton}
    />
  );
};

/**
 * Sets the default output/input device
 */
const setDefaultDevice = (n, v) => {
  const name = n as string
  const value = v as string
  switch (name) {
    case deviceTypes.INPUT: {
      dispatch(setDefaultAudioInput(value));
      break;
    }
    case deviceTypes.OUTPUT: {
      dispatch(setDefaultAudioOutput(value));
      break;
    }
    default: {
      console.error(`${name} not in deviceTypes`);
    }
  }
};

/**
 * Shows the call notification sound options
 */
const showCallNotificationSounds = () => {
  const callNotificationSounds = uiString.CALL_NOTIFICATION_SOUND_FILE_NAMES.map(
    (value, index) => ({
      value: index,
      text: value,
    })
  );
  callNotificationSounds.push({
    value: -1,
    text: uiString.NO_SOUND,
  });

  return (
    <div style={{ padding: "15px 0 10px 0" }}>
      <div style={{ padding: "5px 0" }} className="none-select-text">
        {uiString.preferences.INCOMING_CALL_SOUND}
      </div>
      <DropdownComponent
        id={domIds.AUDIO_PREFERENCES_CALL_NOTIFICATIONS_SOUND_DROPDOWN}
        className="text-light"
        value={selectedCallNotificationSound}
        name={callNotificationOptionName}
        options={callNotificationSounds}
        onChange={setCallNotificationSoundLocal}
        smallIcon
      />
    </div>
  );
};

/**
 * Shows the text notification sound options
 */
const showTextNotificationSounds = () => {
  const textNotificationSounds = uiString.TEXT_NOTIFICATION_SOUND_FILE_NAMES.map(
    (value, index) => ({
      value: index,
      text: value,
    })
  );
  textNotificationSounds.push({
    value: -1,
    text: uiString.NO_SOUND,
  });

  return (
    <div style={{ padding: "15px 0 10px 0" }}>
      <div style={{ padding: "5px 0" }} className="none-select-text">
        {uiString.preferences.INCOMING_TEXT_SOUND}
      </div>
      <DropdownComponent
        id={domIds.AUDIO_PREFERENCES_TEXT_NOTIFICATIONS_SOUND_DROPDOWN}
        className="text-light"
        value={selectedTextNotificationSound}
        name={textNotificationOptionName}
        options={textNotificationSounds}
        onChange={setTextNotificationSoundLocal}
        smallIcon
      />
    </div>
  );
};

/**
 * Sets the call notification sound
 */
const setCallNotificationSoundLocal = (
  event: React.ChangeEvent<
    HTMLInputElement | { name?: string | undefined; value: unknown }
  >
) => {
  const { value } = event.target;
  const noSoundSelected = value === -1
  dispatch(setCallNotificationSound(value as number));
  let filePath = "";

  if (!noSoundSelected) {
    filePath = new FilePathHelper().getFullFilePath(
      `sounds/${uiString.CALL_NOTIFICATION_SOUND_FILES[value as number]}`
    );
  }

  notifyPlayer.pause();
  notifyPlayer.src = filePath;
  !noSoundSelected && notifyPlayer.play();
};

/**
 * Sets the text notification sound
 */
const setTextNotificationSoundLocal = (
  event: React.ChangeEvent<
    HTMLInputElement | { name?: string | undefined; value: unknown }
  >
) => {
  const { value } = event.target;
  const noSoundSelected = value === -1
  dispatch(setTextNotificationSound(value as number));
  let filePath = "";

  if (!noSoundSelected) {
    filePath = new FilePathHelper().getFullFilePath(
      `sounds/${uiString.TEXT_NOTIFICATION_SOUND_FILES[value as number]}`
    );
  }

  notifyPlayer.pause();
  notifyPlayer.src = filePath;
  !noSoundSelected && notifyPlayer.play();
};

/**
 * Create the audio player and set up an input file
 */
const setupAudioPlayer = () => {
  const filePath = new FilePathHelper().getFullFilePath(testSoundFilePath);
  audioPlayer = new Audio();
  audioPlayer.src = filePath;
  audioPlayer.volume = defaultAudioOutputVolume;
  audioPlayer.onended = donePlayingSound;
};

/**
 * Called when a test sound option is changed
 */
const handleTestOutput = () => {

  if (playingTestSound) {
    audioPlayer.pause();
    audioPlayer.currentTime = 0;

    setPlayingTestSound(false);
  } else {
    setPlayingTestSound(true);

    if ("setSinkId" in HTMLMediaElement.prototype) {
      audioPlayer
        .setSinkId(defaultAudioOutput)
        .then(() => audioPlayer.play())
        .catch((error) => {
          setPlayingTestSound(false);
          handleError(error);
        });
    } else {
      audioPlayer.play();
    }
  }
};

/**
 * Called when the test sound is finished playing
 */
const donePlayingSound = () => setPlayingTestSound(false);

/**
 * Called when Test Mic is toggled
 */
const handleTestMic = () => {
  dispatch(toggleTestMic(!isEnabledTestMic));
};

useEffect(() => {
  getAudioDevices();
  setupAudioPlayer();
}, [])

useEffect(() => {
  notifyPlayer = new Audio();
}, [inputDevices, outputDevices])

useEffect(() => {
  audioPlayer.volume = defaultAudioOutputVolume;

  return () => {
    audioPlayer.pause();
    notifyPlayer.pause();
  }
  
}, [defaultAudioOutputVolume])

  return (
    <div>
      <UnderlinedTitle>
        {uiString.preferences.NOTIFICATIONS}
      </UnderlinedTitle>
      {/* Shows call notification sound options */}
      {showCallNotificationSounds()}
      {/* Shows text notification sound options */}
      {showTextNotificationSounds()}
      <div
        style={{
          padding: "20px 0 10px 0",
          width: "90%",
          borderBottom: "1px solid var(--color-dark-grey)",
          fontSize: "18px",
        }}
        className="none-select-text"
      >
        {uiString.preferences.INPUT}
        {(isFirefox || isSafari) && <WarningForSafariOrFirefox />}
      </div>
      {showAudioOptions({
        label: uiString.INPUT_DEVICE,
        devices: inputDevices,
        selected: defaultAudioInput,
        type: deviceTypes.INPUT,
        forceSelect: isFirefox,
      })}
      <MicLevelVisualizer
        defaultAudioInputVolume={defaultAudioInputVolume}
        defaultAudioInput={defaultAudioInput}
        isEnabled={isEnabledTestMic}
      />
      <VolumeSlider deviceType={deviceTypes.INPUT} />
      <div
        style={{
          padding: "20px 0 10px 0",
          width: "90%",
          borderBottom: "1px solid var(--color-dark-grey)",
          fontSize: "18px",
        }}
        className="none-select-text"
      >
        {uiString.preferences.OUTPUT}
        {(isFirefox || isSafari) && <WarningForSafariOrFirefox />}
      </div>
      {showAudioOptions({
        label: uiString.OUTPUT_DEVICE,
        devices: outputDevices,
        selected: defaultAudioOutput,
        type: deviceTypes.OUTPUT,
      })}
      <VolumeSlider deviceType={deviceTypes.OUTPUT} />
    </div>
  );
};

const useStyles = makeStyles({
  radioButton: {
    margin: '8px 24px 8px 33px'
  },
})

export default NotificationsAndSounds
